/**
 * CleanUpStringsCard Component
 *
 * Scans for strings that are no longer referenced by any object, dialog,
 * or variable reference. Provides deprecation (step 1) and deletion (step 2)
 * of unreferenced strings.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  findUnusedStrings,
  type UnusedStringsReport,
  type UnusedStringsProgress,
} from '@/lib/services/UnusedStringsService';
import { markStringsAsDeprecated, deleteString } from '@/lib/stores/stringsStore';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface CleanUpStringsCardProps {
  gameId: string;
}

export function CleanUpStringsCard({ gameId }: CleanUpStringsCardProps) {
  const { toast } = useToast();

  const [isScanning, setIsScanning] = useState(false);
  const [isDeprecating, setIsDeprecating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scanProgress, setScanProgress] = useState<UnusedStringsProgress | null>(null);
  const [report, setReport] = useState<UnusedStringsReport | null>(null);
  const [showUnusedActive, setShowUnusedActive] = useState(false);
  const [showUnusedDeprecated, setShowUnusedDeprecated] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number } | null>(null);

  const handleScan = () => {
    setIsScanning(true);
    setScanProgress(null);
    setReport(null);
    setConfirmDelete(false);

    try {
      const result = findUnusedStrings(gameId, (progress) => {
        setScanProgress(progress);
      });

      setReport(result);

      const totalUnused = result.unusedActive.length + result.unusedDeprecated.length;
      if (totalUnused > 0) {
        toast({
          title: 'Unused Strings Found',
          description: `Found ${totalUnused} unreferenced string${totalUnused !== 1 ? 's' : ''}.`,
          variant: 'default',
        });
      } else {
        toast({
          title: 'All Clean',
          description: 'Every string is referenced. Nothing to clean up!',
        });
      }
    } catch (error) {
      console.error('Error scanning for unused strings:', error);
      toast({
        title: 'Error',
        description: 'Failed to scan for unused strings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsScanning(false);
      setScanProgress(null);
    }
  };

  const handleDeprecateAll = async () => {
    if (!report || report.unusedActive.length === 0) return;

    setIsDeprecating(true);

    try {
      const ids = report.unusedActive.map(s => s.id);
      await markStringsAsDeprecated(ids, gameId, toast);

      toast({
        title: 'Strings Deprecated',
        description: `Marked ${ids.length} string${ids.length !== 1 ? 's' : ''} as deprecated.`,
      });

      // Move deprecated strings to the deprecated list and clear active
      setReport({
        ...report,
        unusedDeprecated: [...report.unusedDeprecated, ...report.unusedActive],
        unusedActive: [],
      });
    } catch (error) {
      console.error('Error deprecating strings:', error);
      toast({
        title: 'Error',
        description: 'Failed to deprecate strings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeprecating(false);
    }
  };

  const handleDeleteAllDeprecated = async () => {
    if (!report || report.unusedDeprecated.length === 0) return;

    setIsDeleting(true);
    setDeleteProgress({ current: 0, total: report.unusedDeprecated.length });

    const errors: string[] = [];
    let deleted = 0;

    for (const str of report.unusedDeprecated) {
      try {
        await deleteString(str.id, gameId, toast);
        deleted++;
        setDeleteProgress({ current: deleted, total: report.unusedDeprecated.length });
      } catch (error) {
        console.error(`Error deleting string ${str.id}:`, error);
        errors.push(str.id);
      }
    }

    if (errors.length === 0) {
      toast({
        title: 'Strings Deleted',
        description: `Permanently deleted ${deleted} string${deleted !== 1 ? 's' : ''}.`,
      });
      setReport({
        ...report,
        unusedDeprecated: [],
        totalStrings: report.totalStrings - deleted,
      });
    } else {
      toast({
        title: 'Partial Deletion',
        description: `Deleted ${deleted} string${deleted !== 1 ? 's' : ''}, but ${errors.length} failed.`,
        variant: 'destructive',
      });
      setReport({
        ...report,
        unusedDeprecated: report.unusedDeprecated.filter(s => errors.includes(s.id)),
        totalStrings: report.totalStrings - deleted,
      });
    }

    setIsDeleting(false);
    setDeleteProgress(null);
    setConfirmDelete(false);
  };

  const totalUnused = report ? report.unusedActive.length + report.unusedDeprecated.length : 0;
  const hasResults = report !== null;
  const isBusy = isScanning || isDeprecating || isDeleting;

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <IconProvider iconstring="fa-broom" size={12} className="text-primary" />
        <div className="flex-1">
          <h2 className="heading-4">Clean Up Strings</h2>
          <p className="text-muted-foreground mb-4">
            Find translation strings that are no longer referenced by any library object, dialog, or variable.
            Unreferenced strings can be deprecated first, then permanently deleted.
          </p>

          {!hasResults && !isScanning && (
            <Button onClick={handleScan} size="lg" className="gap-2">
              <IconProvider iconstring="fa-search" size={16} />
              Scan for Unused Strings
            </Button>
          )}

          {isScanning && (
            <Alert className="mb-4">
              <IconProvider iconstring="fa-spinner" size={14} className="mr-2 animate-spin" />
              <AlertDescription>
                <div className="space-y-2">
                  <div className="font-medium">{scanProgress?.message || 'Scanning...'}</div>
                  {scanProgress && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${scanProgress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {hasResults && !isScanning && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="text-sm text-muted-foreground">
                Scanned {report.totalStrings} strings — {report.referencedCount} referenced, {totalUnused} unused.
              </div>

              {totalUnused === 0 ? (
                <>
                  <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
                    <IconProvider iconstring="fa-check-circle" size={14} className="mr-2 text-green-600" />
                    <AlertDescription>
                      <div className="font-medium text-green-900 dark:text-green-100">
                        No unused strings!
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        Every string in your game is referenced by at least one entity.
                      </p>
                    </AlertDescription>
                  </Alert>

                  <Button onClick={handleScan} size="lg" variant="outline" className="gap-2">
                    <IconProvider iconstring="fa-refresh" size={16} />
                    Scan Again
                  </Button>
                </>
              ) : (
                <>
                  {/* Unused Active Strings */}
                  {report.unusedActive.length > 0 && (
                    <div className="space-y-2">
                      <div className="border rounded">
                        <button
                          onClick={() => setShowUnusedActive(!showUnusedActive)}
                          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {showUnusedActive ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <IconProvider iconstring="fa-unlink" size={14} className="text-amber-600" />
                            <span className="font-medium">Unreferenced Strings</span>
                            <span className="text-sm text-muted-foreground">
                              ({report.unusedActive.length})
                            </span>
                          </div>
                        </button>

                        {showUnusedActive && (
                          <div className="border-t p-3 space-y-2 max-h-72 overflow-y-auto">
                            {report.unusedActive.map((str) => (
                              <div key={str.id} className="text-sm p-2 rounded bg-muted/30">
                                <div className="font-medium truncate">
                                  {str.source_text || <span className="italic text-muted-foreground">empty</span>}
                                </div>
                                {str.abbrev && (
                                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                                    {str.abbrev}
                                  </div>
                                )}
                                {str.context && (
                                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                    {str.context}
                                  </div>
                                )}
                                <div className="text-xs mt-1">
                                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                    {str.status || 'active'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={handleDeprecateAll}
                        disabled={isBusy}
                        size="default"
                        variant="default"
                        className="gap-2"
                      >
                        {isDeprecating ? (
                          <>
                            <IconProvider iconstring="fa-spinner" className="animate-spin" />
                            Deprecating...
                          </>
                        ) : (
                          <>
                            <IconProvider iconstring="fa-archive" size={16} />
                            Deprecate All ({report.unusedActive.length})
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Deprecated Strings (ready for deletion) */}
                  {report.unusedDeprecated.length > 0 && (
                    <div className="space-y-2">
                      <div className="border rounded">
                        <button
                          onClick={() => setShowUnusedDeprecated(!showUnusedDeprecated)}
                          className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {showUnusedDeprecated ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <IconProvider iconstring="fa-trash" size={14} className="text-red-600" />
                            <span className="font-medium">Deprecated & Unreferenced</span>
                            <span className="text-sm text-muted-foreground">
                              ({report.unusedDeprecated.length})
                            </span>
                          </div>
                        </button>

                        {showUnusedDeprecated && (
                          <div className="border-t p-3 space-y-2 max-h-72 overflow-y-auto">
                            {report.unusedDeprecated.map((str) => (
                              <div key={str.id} className="text-sm p-2 rounded bg-muted/30">
                                <div className="font-medium truncate">
                                  {str.source_text || <span className="italic text-muted-foreground">empty</span>}
                                </div>
                                {str.abbrev && (
                                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                                    {str.abbrev}
                                  </div>
                                )}
                                {str.context && (
                                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                    {str.context}
                                  </div>
                                )}
                                <div className="text-xs mt-1">
                                  <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                    deprecated
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {isDeleting && deleteProgress && (
                        <Alert className="mb-2">
                          <IconProvider iconstring="fa-spinner" size={14} className="mr-2 animate-spin" />
                          <AlertDescription>
                            <div className="space-y-2">
                              <div className="font-medium">
                                Deleting strings... ({deleteProgress.current}/{deleteProgress.total})
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div
                                  className="bg-red-500 h-2 rounded-full transition-all"
                                  style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                                />
                              </div>
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}

                      {!confirmDelete ? (
                        <Button
                          onClick={() => setConfirmDelete(true)}
                          disabled={isBusy}
                          size="default"
                          variant="destructive"
                          className="gap-2"
                        >
                          <IconProvider iconstring="fa-trash" size={16} />
                          Delete All Deprecated ({report.unusedDeprecated.length})
                        </Button>
                      ) : (
                        <Alert className="border-red-200 bg-red-50 dark:bg-red-950/20">
                          <AlertDescription>
                            <div className="font-medium text-red-900 dark:text-red-100 mb-2">
                              Permanently delete {report.unusedDeprecated.length} deprecated string{report.unusedDeprecated.length !== 1 ? 's' : ''}?
                            </div>
                            <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                              This action cannot be undone. All translations for these strings will also be removed.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleDeleteAllDeprecated}
                                disabled={isBusy}
                                size="sm"
                                variant="destructive"
                                className="gap-2"
                              >
                                {isDeleting ? (
                                  <>
                                    <IconProvider iconstring="fa-spinner" className="animate-spin" />
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <IconProvider iconstring="fa-trash" size={14} />
                                    Yes, Delete Permanently
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={() => setConfirmDelete(false)}
                                disabled={isBusy}
                                size="sm"
                                variant="outline"
                              >
                                Cancel
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  <Button onClick={handleScan} disabled={isBusy} size="lg" variant="outline" className="gap-2">
                    <IconProvider iconstring="fa-refresh" size={16} />
                    Scan Again
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CleanUpStringsCard;
